/**
 * SKU Generation Service
 * 
 * Provides configurable SKU pattern generation with conflict resolution,
 * batch generation support, and variant-aware generation capabilities.
 */

export interface SKUPattern {
  id: string
  name: string
  pattern: string
  description: string
  example: string
  fields: string[]
}

export interface SKUGenerationOptions {
  pattern?: string
  prefix?: string
  category?: string
  brand?: string
  supplier?: string
  product?: string
  variant?: string
  sequence?: number
  date?: Date
  randomLength?: number
}

export interface SKUGenerationResult {
  sku: string
  pattern: string
  isUnique: boolean
  conflicts?: string[]
  metadata?: {
    generatedAt: Date
    pattern: string
    fields: Record<string, string>
  }
}

export interface BatchSKUGenerationResult {
  skus: SKUGenerationResult[]
  successful: number
  failed: number
  conflicts: string[]
}

// Predefined SKU pattern templates
export const SKU_PATTERNS: SKUPattern[] = [
  {
    id: 'prefix-category-sequence',
    name: 'Prefix + Category + Sequence',
    pattern: '{PREFIX}-{CATEGORY}-{SEQUENCE:000}',
    description: 'Company prefix, category code, and auto-incrementing sequence',
    example: 'ADM-ELEC-001',
    fields: ['PREFIX', 'CATEGORY', 'SEQUENCE']
  },
  {
    id: 'brand-product-variant',
    name: 'Brand + Product + Variant',
    pattern: '{BRAND}-{PRODUCT}-{VARIANT}',
    description: 'Brand code, product identifier, and variant specification',
    example: 'SONY-WH1000XM4-BLK',
    fields: ['BRAND', 'PRODUCT', 'VARIANT']
  },
  {
    id: 'supplier-date-random',
    name: 'Supplier + Date + Random',
    pattern: '{SUPPLIER}-{DATE:YYYYMM}-{RANDOM:AAA}',
    description: 'Supplier code, year-month, and random identifier',
    example: 'SUP001-202412-A7K',
    fields: ['SUPPLIER', 'DATE', 'RANDOM']
  },
  {
    id: 'category-brand-sequence-variant',
    name: 'Category + Brand + Sequence + Variant',
    pattern: '{CATEGORY}-{BRAND}-{SEQUENCE:0000}-{VARIANT}',
    description: 'Category, brand, sequence number, and variant code',
    example: 'ELEC-SONY-0001-L',
    fields: ['CATEGORY', 'BRAND', 'SEQUENCE', 'VARIANT']
  },
  {
    id: 'hierarchical-category',
    name: 'Hierarchical Category',
    pattern: '{CATEGORY1}-{CATEGORY2}-{BRAND}-{SEQUENCE:000}',
    description: 'Multi-level category hierarchy with brand and sequence',
    example: 'TECH-AUDIO-SONY-001',
    fields: ['CATEGORY1', 'CATEGORY2', 'BRAND', 'SEQUENCE']
  }
]

class SKUGenerationService {
  private static instance: SKUGenerationService
  private existingSKUs: Set<string> = new Set()
  private sequenceCounters: Map<string, number> = new Map()

  static getInstance(): SKUGenerationService {
    if (!this.instance) {
      this.instance = new SKUGenerationService()
    }
    return this.instance
  }

  /**
   * Initialize service with existing SKUs for conflict detection
   */
  public initialize(existingSKUs: string[]): void {
    this.existingSKUs = new Set(existingSKUs)
    this.updateSequenceCounters()
  }

  /**
   * Generate single SKU based on pattern and options
   */
  public generateSKU(options: SKUGenerationOptions): SKUGenerationResult {
    const pattern = this.getPattern(options.pattern)
    if (!pattern) {
      throw new Error(`Unknown SKU pattern: ${options.pattern}`)
    }

    const sku = this.applyPattern(pattern, options)
    const isUnique = this.checkUniqueness(sku)
    const conflicts = isUnique ? undefined : this.findConflicts(sku)

    return {
      sku,
      pattern: pattern.pattern,
      isUnique,
      conflicts,
      metadata: {
        generatedAt: new Date(),
        pattern: pattern.name,
        fields: this.extractFields(pattern, options)
      }
    }
  }

  /**
   * Generate multiple SKUs with conflict resolution
   */
  public generateBatchSKUs(
    optionsArray: SKUGenerationOptions[]
  ): BatchSKUGenerationResult {
    const results: SKUGenerationResult[] = []
    const allConflicts: string[] = []
    let successful = 0
    let failed = 0

    for (const options of optionsArray) {
      try {
        let result = this.generateSKU(options)
        
        // If conflict detected, try to resolve
        if (!result.isUnique && this.shouldAutoResolve(options)) {
          result = this.resolveConflict(options, result)
        }

        results.push(result)
        
        if (result.isUnique) {
          successful++
          this.existingSKUs.add(result.sku)
        } else {
          failed++
          if (result.conflicts) {
            allConflicts.push(...result.conflicts)
          }
        }
      } catch (error) {
        failed++
        results.push({
          sku: '',
          pattern: options.pattern || '',
          isUnique: false,
          conflicts: [error instanceof Error ? error.message : 'Unknown error']
        })
      }
    }

    return {
      skus: results,
      successful,
      failed,
      conflicts: [...new Set(allConflicts)]
    }
  }

  /**
   * Generate variant-aware SKUs for product variants
   */
  public generateVariantSKUs(
    baseOptions: SKUGenerationOptions,
    variants: Array<{ id: string; attributes: Record<string, string> }>
  ): BatchSKUGenerationResult {
    const variantOptions = variants.map(variant => ({
      ...baseOptions,
      variant: this.generateVariantCode(variant.attributes),
      sequence: this.getNextSequence(baseOptions.pattern || 'default')
    }))

    return this.generateBatchSKUs(variantOptions)
  }

  /**
   * Validate SKU format against pattern
   */
  public validateSKU(sku: string, patternId: string): boolean {
    const pattern = this.getPattern(patternId)
    if (!pattern) return false

    const regex = this.patternToRegex(pattern.pattern)
    return regex.test(sku)
  }

  /**
   * Get available SKU patterns
   */
  public getAvailablePatterns(): SKUPattern[] {
    return [...SKU_PATTERNS]
  }

  /**
   * Preview SKU generation without saving
   */
  public previewSKU(options: SKUGenerationOptions): string {
    const pattern = this.getPattern(options.pattern)
    if (!pattern) return ''

    return this.applyPattern(pattern, options)
  }

  // Private methods

  private getPattern(patternId?: string): SKUPattern | null {
    if (!patternId) return SKU_PATTERNS[0] // Default pattern
    return SKU_PATTERNS.find(p => p.id === patternId) || null
  }

  private applyPattern(pattern: SKUPattern, options: SKUGenerationOptions): string {
    let result = pattern.pattern

    // Replace pattern variables
    const replacements = {
      PREFIX: options.prefix || 'PRD',
      CATEGORY: this.formatCode(options.category || 'GEN'),
      CATEGORY1: this.formatCode(options.category?.split('-')[0] || 'GEN'),
      CATEGORY2: this.formatCode(options.category?.split('-')[1] || 'SUB'),
      BRAND: this.formatCode(options.brand || 'BRD'),
      SUPPLIER: this.formatCode(options.supplier || 'SUP'),
      PRODUCT: this.formatCode(options.product || 'PRD'),
      VARIANT: this.formatCode(options.variant || 'VAR'),
      SEQUENCE: this.formatSequence(options.sequence || this.getNextSequence(pattern.id)),
      DATE: this.formatDate(options.date || new Date()),
      RANDOM: this.generateRandom(options.randomLength || 3)
    }

    // Apply replacements with format specifiers
    for (const [key, value] of Object.entries(replacements)) {
      const regex = new RegExp(`\\{${key}(?::(\\w+))?\\}`, 'g')
      result = result.replace(regex, (match, format) => {
        if (format && key === 'SEQUENCE') {
          return value.toString().padStart(format.length, '0')
        }
        if (format && key === 'DATE') {
          return this.formatDateWithPattern(options.date || new Date(), format)
        }
        if (format && key === 'RANDOM') {
          return this.generateRandomWithPattern(format)
        }
        return value.toString()
      })
    }

    return result.toUpperCase()
  }

  private formatCode(input: string): string {
    return input
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 5)
      .toUpperCase()
  }

  private formatSequence(sequence: number): string {
    return sequence.toString()
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    return `${year}${month}`
  }

  private formatDateWithPattern(date: Date, pattern: string): string {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')

    return pattern
      .replace('YYYY', year.toString())
      .replace('MM', month)
      .replace('DD', day)
  }

  private generateRandom(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  private generateRandomWithPattern(pattern: string): string {
    const charMap: Record<string, string> = {
      'A': 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      '0': '0123456789',
      'X': 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    }

    let result = ''
    for (const char of pattern) {
      const chars = charMap[char] || char
      if (chars.length > 1) {
        result += chars.charAt(Math.floor(Math.random() * chars.length))
      } else {
        result += char
      }
    }
    return result
  }

  private generateVariantCode(attributes: Record<string, string>): string {
    const codes = Object.values(attributes)
      .map(value => value.slice(0, 3).toUpperCase())
      .join('')
    return codes.slice(0, 6)
  }

  private getNextSequence(key: string): number {
    const current = this.sequenceCounters.get(key) || 0
    const next = current + 1
    this.sequenceCounters.set(key, next)
    return next
  }

  private updateSequenceCounters(): void {
    // Extract sequence numbers from existing SKUs to update counters
    for (const sku of this.existingSKUs) {
      const matches = sku.match(/(\d+)/g)
      if (matches) {
        const lastNumber = parseInt(matches[matches.length - 1])
        if (!isNaN(lastNumber)) {
          const currentMax = this.sequenceCounters.get('default') || 0
          this.sequenceCounters.set('default', Math.max(currentMax, lastNumber))
        }
      }
    }
  }

  private checkUniqueness(sku: string): boolean {
    return !this.existingSKUs.has(sku)
  }

  private findConflicts(sku: string): string[] {
    const conflicts: string[] = []
    if (this.existingSKUs.has(sku)) {
      conflicts.push(sku)
    }
    return conflicts
  }

  private shouldAutoResolve(options: SKUGenerationOptions): boolean {
    return options.sequence === undefined // Only auto-resolve if sequence not manually specified
  }

  private resolveConflict(
    options: SKUGenerationOptions,
    conflictResult: SKUGenerationResult
  ): SKUGenerationResult {
    // Try up to 10 times to resolve conflict by incrementing sequence
    for (let i = 1; i <= 10; i++) {
      const newOptions = {
        ...options,
        sequence: (options.sequence || 0) + i
      }
      
      const result = this.generateSKU(newOptions)
      if (result.isUnique) {
        return result
      }
    }
    
    // If still conflicted, return original result
    return conflictResult
  }

  private extractFields(pattern: SKUPattern, options: SKUGenerationOptions): Record<string, string> {
    return pattern.fields.reduce((acc, field) => {
      switch (field) {
        case 'PREFIX': acc[field] = options.prefix || 'PRD'; break
        case 'CATEGORY': acc[field] = options.category || 'GEN'; break
        case 'BRAND': acc[field] = options.brand || 'BRD'; break
        case 'SUPPLIER': acc[field] = options.supplier || 'SUP'; break
        case 'PRODUCT': acc[field] = options.product || 'PRD'; break
        case 'VARIANT': acc[field] = options.variant || 'VAR'; break
        case 'SEQUENCE': acc[field] = (options.sequence || 1).toString(); break
        case 'DATE': acc[field] = this.formatDate(options.date || new Date()); break
        case 'RANDOM': acc[field] = this.generateRandom(3); break
      }
      return acc
    }, {} as Record<string, string>)
  }

  private patternToRegex(pattern: string): RegExp {
    let regex = pattern
      .replace(/\{[^}]+\}/g, '([A-Z0-9]+)')
      .replace(/\-/g, '\\-')
    
    return new RegExp(`^${regex}$`)
  }
}

// Export singleton instance
export const skuGenerationService = SKUGenerationService.getInstance()
export default SKUGenerationService